import { motion } from "framer-motion";
import { Leaf, Recycle, Wind } from "lucide-react";

const LAB_IMG =
  "https://images.unsplash.com/photo-1669216369094-1d7670ec950a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjbGVhbiUyMHNjaWVuY2UlMjBsYWJvcmF0b3J5JTIwZGFya3xlbnwwfHx8fDE3NzgxNTI5MDN8MA&ixlib=rb-4.1.0&q=85";

const points = [
  { Icon: Recycle, label: "Lignocellulosic upcycling" },
  { Icon: Wind, label: "Lower carbon emissions" },
  { Icon: Leaf, label: "Life-saving nutrients" },
];

export const ProblemSolver = () => {
  return (
    <section
      id="impact"
      data-testid="impact-section"
      className="relative py-24 md:py-40 overflow-hidden bg-[#040806]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        {/* Left text */}
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#10B981] flex items-center gap-3 mb-6">
            <span className="h-px w-6 bg-[#10B981]" />
            03 / National Impact
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white leading-[1.02]"
          >
            Cleaner skies.
            <br />
            <span className="text-white/50">Purer molecules</span>
            <span className="text-[#10B981]">.</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mt-8 font-body text-base md:text-lg text-white/70 leading-[1.75] max-w-xl"
          >
            We are tackling one of the nation&apos;s greatest environmental
            challenges:{" "}
            <span className="text-white">Lignocellulosic waste</span>. By
            intercepting and upcycling this biomass into high-value bio-actives,
            we prevent environmental degradation and carbon emissions. Colossus
            is converting yesterday&apos;s burden into tomorrow&apos;s
            life-saving nutrients — solving India&apos;s waste crisis through
            advanced science.
          </motion.p>

          <div className="mt-10 flex flex-wrap gap-3">
            {points.map(({ Icon, label }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * i }}
                className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-4 py-2 text-xs uppercase tracking-[0.18em] text-white/70"
                data-testid={`impact-point-${i}`}
              >
                <Icon className="h-3.5 w-3.5 text-[#10B981]" />
                {label}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right image card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1 }}
          className="relative"
        >
          <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] bg-white/[0.02] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]">
            <img
              src={LAB_IMG}
              alt="Colossus laboratory"
              className="w-full h-[420px] md:h-[560px] object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030704] via-transparent to-transparent" />

            {/* Floating data badge */}
            <div className="absolute top-5 left-5 inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur-md border border-white/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] pulse-soft" />
              Live · R&D Cluster
            </div>

            {/* Corner stat */}
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
              <div>
                <div className="font-display text-5xl md:text-6xl font-light text-white">
                  Zero
                </div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mt-1">
                  Animal Inputs · 100% Botanical
                </div>
              </div>
              <div className="font-display text-right">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#10B981]">
                  Upcycle Index
                </div>
                <div className="text-2xl font-light text-white">
                  98.4<span className="text-[#10B981]">%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Decorative orbital ring */}
          <div className="absolute -bottom-8 -right-8 h-40 w-40 rounded-full border border-[#10B981]/20 orbit-slow pointer-events-none" />
          <div className="absolute -bottom-6 -right-6 h-36 w-36 rounded-full border border-[#10B981]/10 orbit-rev pointer-events-none" />
        </motion.div>
      </div>
    </section>
  );
};

export default ProblemSolver;
